import React, { useEffect, useState, useCallback } from 'react';
import { Container, Box, Tabs, Tab, Snackbar, Alert, TablePagination } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import ApiServices from '../../services/ApiServices';
import SearchFilter from '../SearchFilter';

import DeviceHardwareForm from './DeviceHardwareForm';
import HardwareMismatchTable from './HardwareMismatchTable';
import { DATA_COLUMNS, INITIAL_FORM_STATE, HARDWARE_FIELDS } from './HardwareConstants';
const getCleanInitialState = () => {
    const base = { imei: '' };
    HARDWARE_FIELDS.forEach(field => {
        base[field.key] = ""; 
    });
    return base;
};
const DeviceHardware = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState([]);
    const [currentTab, setCurrentTab] = useState('view');
    const [formData, setFormData] = useState(() => getCleanInitialState());
    const [submitLoading, setSubmitLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const columns = DATA_COLUMNS.map(col => ({
        field: col.key,
        headerName: col.label,
        flex: 1,
        valueGetter: (params) => col.access(params.row),
        type: col.key === 'relaySupport' || col.key === 'daliBusPsSupport' ? 'boolean' : 'string',
    }));
    columns[0].flex = 1.2;

    const handleFormChange = (e) => {
        const { name, value } = e.target;        
        setFormData(prev => ({ 
            ...prev, 
            [name]: value 
        }));
    };


    const showSnackbar = (message, severity) => {  
        let finalMessage = message;
        if (typeof message === 'object' && message !== null) {
            finalMessage = message.message || JSON.stringify(message);
        }

        setSnackbar({
            open: true,
            message: String(finalMessage),
            severity: severity,
        });
    };

    const handleApiError = useCallback((error, customMessage) => {
        const msg = error.response?.data?.message || error.response?.data || error.message || customMessage;
        showSnackbar(msg,'error');
    }, []);

    const fetchDevices = async () => {
        setLoading(true);
        const params = new URLSearchParams({ page, size: rowsPerPage });
        const imeiList = filters.find(f => f.field === "deviceIMEI")?.value?.split(",") || [];

        filters.filter(f => f.field !== "deviceIMEI").forEach(f => params.append(f.field, f.value));

        try {
            const response = await ApiServices.searchDeviceHardware(params.toString(), { deviceList: imeiList });
            if (response.status === 200) {
                setDevices(response.data.filter(row => row && row.device));
                setTotalRows(Number(response.headers['x-total']) || 0);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch devices");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        const imeiArray = formData.imei.split(',').map(i => i.trim()).filter(i => i.length > 0);
        const hardwareConfig = HARDWARE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: formData[f.key] }), {});

        try {
            const response = await ApiServices.flashHardwareId({ imeiList: imeiArray, hardwareConfig });
            showSnackbar(`Applied: ${response.data}`,  'success' );
            setFormData(INITIAL_FORM_STATE);
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            handleApiError(error, "Failed to apply configuration");
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCsvUpload = async (file) => {
        if (!file) return;

        try {
            setSubmitLoading(true);
            const response = await ApiServices.uploadHardwareIds(file);
            if (response.status === 200 || response.status === 201) {
                const successText = response.data?.message || "Upload started successfully";
                showSnackbar(successText, "success");                
                setTimeout(() => {
                    setRefreshKey(prev => prev + 1);
                }, 2000);
            }
        } catch (error) {            
            const errorData = error.response?.data;
            const errorMsg = typeof errorData === 'object' 
                ? (errorData.message || "Upload failed") 
                : (errorData || "Network Error");

            showSnackbar(String(errorMsg), "error");
        } finally {
            setSubmitLoading(false);
        }
    };

    useEffect(() => {
        if (currentTab === 'view') fetchDevices();
    }, [ page, rowsPerPage, filters, currentTab]);

    return (
        <Container maxWidth={false}>
            <Box mt={3}>                
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                    <Tabs
                        value={currentTab}
                        onChange={(e, v) => {
                            setCurrentTab(v);      
                            setFilters([]);
                            setPage(0);
                        }}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab label="Device Hardware View" value="view" />
                        <Tab label="Flash Hardware ID" value="flash" />
                    </Tabs>
                </Box>

                {currentTab === 'view' ? (
                    <Box>
                        <SearchFilter filters={filters} onFilterChange={setFilters} />
                        <DataGrid
                            rows={devices}
                            getRowId={(row) => row.device?.deviceIMEI || Math.random()}
                            columns={columns}
                            loading={loading}
                            autoHeight
                            hideFooter
                        />
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                        />
                    </Box>
                ) : (                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 400, flexShrink: 0 }}>
                            <DeviceHardwareForm
                                formData={formData}
                                handleFormChange={handleFormChange}
                                handleFormSubmit={handleFormSubmit}
                                submitLoading={submitLoading}
                                handleCsvUpload={handleCsvUpload}
                            />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <HardwareMismatchTable refreshKey={refreshKey} />
                        </Box>
                    </Box>
                )}
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default DeviceHardware;